/**
 * Human labels for the six interaction types, typed as an EXHAUSTIVE
 * `Record<InteractionType, string>` for the same reason `INTERACTION_EDITORS` is: a seventh type
 * must break `pnpm typecheck` here too, rather than quietly render a raw `snake_case` name.
 *
 * Glossary note (RND-34): these are Interactions, never "questions" or "widgets".
 */
import type { Activity } from './interfaces'
import type { InteractionType, RubricCatalog } from './types'

export const INTERACTION_LABELS: Record<InteractionType, string> = {
  multiple_choice: 'Multiple choice',
  fill_in_blanks: 'Fill in the blanks',
  drag_the_words: 'Drag the words',
  whiteboard: 'Whiteboard',
  open_ended: 'Open ended',
  audio_response: 'Audio response',
}

/** The order the type selector offers them in — the schema's own union order, spelled out because
 *  `Object.keys` on a `Record` is not a contract. */
export const INTERACTION_TYPES: InteractionType[] = [
  'multiple_choice',
  'fill_in_blanks',
  'drag_the_words',
  'whiteboard',
  'open_ended',
  'audio_response',
]

/** What an activity with no interaction is called. `interaction` is optional in the schema — a
 *  prompt-only activity is a legitimate authored state, not a half-finished one. */
export const NO_INTERACTION_LABEL = 'No interaction'

export const TUTOR_OBSERVATION_LABEL = 'Tutor observation'

/**
 * Nothing for the kid to answer, but rubrics for the tutor to score live — the kid reads aloud in a
 * lesson while the tutor listens. app-tutor applies the same rule, so both apps agree on which
 * activities these are. Only a rubric with score levels counts: a dangling id or a stored
 * observation rubric gives the tutor nothing to score. Read defensively, because the working
 * document is only validated on demand.
 */
export const isTutorObservation = (activity: Activity, catalog: RubricCatalog): boolean =>
  !activity.interaction
  && Array.isArray(activity.rubrics)
  && activity.rubrics.some(id => Array.isArray(catalog[id]?.levels))

/** What the activity is, in one phrase: its interaction, a Tutor observation, or prompt-only. */
export const getActivityKindLabel = (activity: Activity, catalog: RubricCatalog): string => {
  if (activity.interaction) {
    return INTERACTION_LABELS[activity.interaction.type]
  }
  return isTutorObservation(activity, catalog) ? TUTOR_OBSERVATION_LABEL : NO_INTERACTION_LABEL
}

/**
 * Editor copy for the two interactions authored by marking up a sentence. SHARED so it cannot
 * drift: `fill_in_blanks` and `drag_the_words` are authored identically and differ only in what the
 * kid does with the result, so two hand-written copies would diverge on the first edit and teach
 * authors otherwise. The marker shape is spelled out in the copy because it is the entire syntax.
 */
interface MarkerInteractionCopy {
  label: string
  placeholder: string
  hint: string
}

export const MARKER_INTERACTION_COPY: Record<'fill_in_blanks' | 'drag_the_words', MarkerInteractionCopy> = {
  fill_in_blanks: {
    label: 'Text',
    placeholder: 'Write the sentence, with each answer in braces — 7 + {{five}} = {{twelve}}',
    hint: 'Wrap each answer in {{double braces}}.',
  },
  drag_the_words: {
    label: 'Text',
    placeholder: 'Write the sentence, with each answer in asterisks — 7 + *five* = *twelve*',
    hint: 'Wrap each answer in *asterisks*.',
  },
}
